import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { FaClipboardList, FaExclamationCircle } from "react-icons/fa";

const createCriteriaBreakdown = (template, summary) => {
  if (!template || !summary) return [];

  const breakdown = [];

  const extractQuestions = (elements, pageTitle = "") => {
    if (!elements || !Array.isArray(elements)) return;

    elements.forEach((element) => {
      if (element.name && element.type && element.title) {
        const value = summary[element.name];

        if (value !== undefined && value !== null) {
          const choices = element.choices || [];
          let selectedChoice = null;
          if (choices.length > 0) {
            selectedChoice = choices.find((c) => {
              const choiceValue = c.value !== undefined ? c.value : c;
              return String(choiceValue) === String(value);
            });
          }

          breakdown.push({
            questionLabel: element.title,
            questionName: element.name,
            pageTitle,
            availableOptions: choices.map((c) => ({
              text: c.text || c,
              value: c.value !== undefined ? c.value : c,
            })),
            selectedOption: selectedChoice
              ? {
                  text: selectedChoice.text || selectedChoice,
                  value: selectedChoice.value !== undefined ? selectedChoice.value : selectedChoice,
                }
              : null,
            selectedValue: value,
            score: value,
          });
        }
      }

      if (element.elements) {
        extractQuestions(element.elements, pageTitle);
      }
      if (element.questions) {
        extractQuestions(element.questions, pageTitle);
      }
    });
  };

  if (template.pages && Array.isArray(template.pages)) {
    template.pages.forEach((page) => {
      const pageTitle = page.title || page.name || "";
      if (page.elements) {
        extractQuestions(page.elements, pageTitle);
      }
    });
  } else if (template.elements) {
    extractQuestions(template.elements);
  }

  return breakdown;
};

const CriteriaSummaryModal = ({
  isOpen,
  toggle,
  criteriaSummary,
  criteriaTemplate,
  beneficiaryName,
}) => {
  const renderBreakdown = () => {
    try {
      const criteriaBreakdown = createCriteriaBreakdown(criteriaTemplate, criteriaSummary);
      const groupedByPage = {};
      criteriaBreakdown.forEach((item) => {
        const pageTitle = item.pageTitle || "Other";
        if (!groupedByPage[pageTitle]) {
          groupedByPage[pageTitle] = [];
        }
        groupedByPage[pageTitle].push(item);
      });

      if (criteriaBreakdown.length > 0) {
        return (
          <div>
            <h5 style={{ marginBottom: "20px", color: "#495057", borderBottom: "2px solid #dee2e6", paddingBottom: "10px" }}>
              Criteria Breakdown
            </h5>

            {Object.keys(groupedByPage).map((pageTitle, pageIndex) => (
              <div key={pageIndex} style={{ marginBottom: "25px" }}>
                <h6
                  style={{
                    color: "#007bff",
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "15px",
                    padding: "10px 15px",
                    backgroundColor: "#e7f3ff",
                    borderRadius: "6px",
                    borderLeft: "4px solid #007bff",
                  }}
                >
                  {pageTitle}
                </h6>

                <div style={{ display: "grid", gap: "12px" }}>
                  {groupedByPage[pageTitle].map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                        padding: "15px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#495057",
                          marginBottom: "12px",
                          fontSize: "14px",
                          lineHeight: "1.5",
                        }}
                      >
                        {item.questionLabel}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
                        <div style={{ flex: "1", minWidth: "200px" }}>
                          <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "6px", fontWeight: "500" }}>
                            Selected Answer:
                          </div>
                          <div
                            style={{
                              display: "inline-block",
                              padding: "6px 12px",
                              backgroundColor: "#d4edda",
                              color: "#155724",
                              borderRadius: "5px",
                              fontWeight: "500",
                              fontSize: "13px",
                              border: "1px solid #c3e6cb",
                            }}
                          >
                            {item.selectedOption ? item.selectedOption.text : `Value: ${item.selectedValue}`}
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500" }}>Score:</div>
                          <div
                            style={{
                              padding: "6px 14px",
                              backgroundColor: "#007bff",
                              color: "#ffffff",
                              borderRadius: "5px",
                              fontWeight: "bold",
                              fontSize: "16px",
                              minWidth: "50px",
                              textAlign: "center",
                              boxShadow: "0 2px 4px rgba(0,123,255,0.2)",
                            }}
                          >
                            {item.score}
                          </div>
                        </div>
                      </div>

                      {item.availableOptions && item.availableOptions.length > 0 && (
                        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e9ecef" }}>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6c757d",
                              marginBottom: "6px",
                              fontWeight: "500",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Available Options:
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {item.availableOptions.map((option, optIndex) => {
                              const isSelected =
                                item.selectedOption &&
                                String(item.selectedOption.value) === String(option.value);
                              return (
                                <span
                                  key={optIndex}
                                  style={{
                                    padding: "4px 10px",
                                    fontSize: "12px",
                                    borderRadius: "4px",
                                    backgroundColor: isSelected ? "#d4edda" : "#f8f9fa",
                                    color: isSelected ? "#155724" : "#495057",
                                    border: `1px solid ${isSelected ? "#c3e6cb" : "#dee2e6"}`,
                                    fontWeight: isSelected ? "600" : "400",
                                  }}
                                >
                                  {option.text} ({option.value})
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      }

      return (
        <div>
          <h5 style={{ marginBottom: "15px", color: "#495057", borderBottom: "2px solid #dee2e6", paddingBottom: "10px" }}>
            Criteria Breakdown
          </h5>
          {!criteriaTemplate && (
            <div
              style={{
                color: "#856404",
                padding: "12px",
                backgroundColor: "#fff3cd",
                borderRadius: "5px",
                marginBottom: "15px",
                border: "1px solid #ffeaa7",
                fontSize: "13px",
              }}
            >
              <strong>Note:</strong> Selection criteria template not found. Showing simplified view.
            </div>
          )}
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
            }}
          >
            {Object.keys(criteriaSummary)
              .filter((key) => key !== "score" && key !== "can_proceed")
              .map((key, index) => {
                const value = criteriaSummary[key];
                const formattedKey = key
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");

                return (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "12px",
                      marginBottom: "8px",
                      backgroundColor: "#ffffff",
                      borderRadius: "6px",
                      border: "1px solid #e9ecef",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontWeight: "500", color: "#495057" }}>{formattedKey}:</span>
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#007bff",
                        backgroundColor: "#e7f3ff",
                        padding: "4px 12px",
                        borderRadius: "4px",
                      }}
                    >
                      {String(value)}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      );
    } catch (error) {
      return (
        <div
          style={{
            color: "#721c24",
            padding: "15px",
            backgroundColor: "#f8d7da",
            borderRadius: "5px",
            border: "1px solid #f5c6cb",
          }}
        >
          <strong>Error rendering criteria summary:</strong>
          <div style={{ marginTop: "10px", fontSize: "13px" }}>{error.message || "Unknown error"}</div>
        </div>
      );
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" style={{ maxWidth: "900px" }}>
      <ModalHeader toggle={toggle}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <FaClipboardList style={{ fontSize: "20px", color: "#007bff" }} />
          <span>Criteria Summary</span>
        </div>
      </ModalHeader>
      <ModalBody style={{ padding: "25px", maxHeight: "70vh", overflow: "auto" }}>
        {criteriaSummary ? (
          <div>
            {beneficiaryName && (
              <div className="mb-3" style={{ fontSize: "0.9rem" }}>
                <strong>Beneficiary:</strong> {beneficiaryName}
              </div>
            )}
            <div className="row mb-4">
              <div className="col-md-6">
                <div
                  style={{
                    backgroundColor: "#e7f3ff",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "2px solid #007bff",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>Total Score</div>
                  <div style={{ fontSize: "32px", fontWeight: "bold", color: "#007bff" }}>
                    {criteriaSummary.score ?? "N/A"}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  style={{
                    backgroundColor: criteriaSummary.can_proceed ? "#d4edda" : "#f8d7da",
                    padding: "15px",
                    borderRadius: "8px",
                    border: `2px solid ${criteriaSummary.can_proceed ? "#28a745" : "#dc3545"}`,
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>Status</div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: criteriaSummary.can_proceed ? "#28a745" : "#dc3545",
                    }}
                  >
                    {criteriaSummary.can_proceed === undefined
                      ? "N/A"
                      : criteriaSummary.can_proceed
                      ? "✓ Can Proceed"
                      : "✗ Cannot Proceed"}
                  </div>
                </div>
              </div>
            </div>
            {renderBreakdown()}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
            <FaExclamationCircle style={{ fontSize: "48px", marginBottom: "15px" }} />
            <div>No criteria summary available</div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CriteriaSummaryModal;
